import React from 'react';
import { useState, useEffect } from 'react';
import { Alert, Input, Card, CardContent, TextField, MenuItem, Select } from '@mui/material';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../redux/slices/notificationSlice';
import CustomButton from '../../../componentsII/Buttons';
import api from '../../../axios/api';

// Define validation schema
const schema = yup.object().shape({
  sector: yup
    .string()
    .required('Sector is required'),
  IndustryName: yup
    .string()
    .required('Industry name is required')
    .min(2, 'Industry name must be at least 2 characters long')
    .max(50, 'Industry name must be less than 50 characters')
    .trim()
});

const AddUpdateIndustryModal = ({ refetch, onClose, sector: initialSector, IndustryId = null, initialData = null }) => {
  const dispatch = useDispatch();
  
  // Form states
  const [IndustryName, setIndustryName] = useState(initialData?.industry_name || '');
  const [selectedSector, setSelectedSector] = useState(initialSector || '');
  const [sectors, setSectors] = useState([]);
  
  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    sector: '',
    IndustryName: '',
    general: ''
  });

  const validateForm = async () => {
    try {
      await schema.validate({ 
        sector: selectedSector,
        IndustryName 
      }, { abortEarly: false });
      return true;
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(prevErrors => ({
        ...prevErrors,
        ...newErrors
      }));
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ sector: '', IndustryName: '', general: '' });

    const isValid = await validateForm();
    if (!isValid) return;

    try {
      setIsSubmitting(true);

      const payload = {
        IndustryId: IndustryId || 0,
        IndustryName: IndustryName.trim(),
        sector:  sectors.find(sector => sector.sector_name === selectedSector)?.id,
      };

      const response = await api.post('/admin/CreateUpdateIndustry', payload);
      
      if (response.data.status === '200') {
        dispatch(showNotification({ 
          message: response.data.message, 
          type: "success" 
        }));
        onClose();
        refetch();
      } else {
        setErrors(prev => ({
          ...prev,
          general: response.data.message || 'Something went wrong'
        }));
      }
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        general: err.message || 'An error occurred while saving'
      }));
      dispatch(showNotification({ 
        message: err.message || 'An error occurred', 
        type: "error" 
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIndustryNameChange = (e) => {
    setIndustryName(e.target.value);
    setErrors(prev => ({ ...prev, IndustryName: '' }));
  };

  const handleSectorChange = (e) => {
    setSelectedSector(e.target.value);
    setErrors(prev => ({ ...prev, sector: '' }));
  };

  const getSectors = async () => {
    try {
      const response = await api.get('/admin/getSector');
      if (response.data.data) {
        setSectors(response.data.data);
      }
    } catch (err) {
      dispatch(showNotification({ 
        message: 'Failed to fetch sectors', 
        type: "error" 
      }));
    }
  };

  useEffect(() => {
    getSectors();
  }, []);

  return (
    <Card className="w-full mx-auto">
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* {errors.general && (
            <Alert severity="error" className="mb-4">
              {errors.general}
            </Alert>
          )} */}

          <div className="space-y-2">
            <label htmlFor="sector" className="text-sm font-medium">
              Sector
            </label>
            <Select
              id="sector"
              value={selectedSector}
              onChange={handleSectorChange}
              placeholder="Select sector"
              disabled={isSubmitting}
              size='small'
              error={Boolean(errors.sector)}
              className="w-full"
            >
              {sectors.map((sector) => (
                <MenuItem key={sector.id} value={sector.sector_name}>
                  {sector.sector_name}
                </MenuItem>
              ))}
            </Select>
            {errors.sector && (
              <p className="text-sm text-red-500 mt-1">{errors.sector}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="IndustryName" className="text-sm font-medium">
              Industry Name
            </label>
            <TextField
              id="IndustryName"
              value={IndustryName}
              onChange={handleIndustryNameChange}
              placeholder="Enter Industry name"
              disabled={isSubmitting}
              size='small'
              error={Boolean(errors.IndustryName)}
              className="w-full"
            />
            {errors.IndustryName && (
              <p className="text-sm text-red-500 mt-1">{errors.IndustryName}</p>
            )}
          </div>

          <div className="flex justify-end gap-5 items-center mt-5">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
              disabled={isSubmitting}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={IndustryId ? "Update" : "Submit"}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddUpdateIndustryModal;